<script setup lang="ts">
import { ref, inject, computed, onMounted, watch, Ref } from 'vue'
import { toast } from 'vue-sonner'
import useGamification from '@/services/Gamification'
import type { GameReward, GameRule, GameMilestone } from '@/services/Gamification'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  Trophy, Star, Target, Zap, Plus, Trash2, Pencil,
  HelpCircle, Award, TrendingUp, Gift, LayoutList
} from 'lucide-vue-next'

const vhostId = inject<Ref<string> | null>('vhostId', null)
const {
  fetchRewards, createReward, deleteReward,
  fetchRules, createRule, deleteRule,
  fetchMilestones, createMilestone, updateMilestone, deleteMilestone,
} = useGamification()

// ─── State ──────────────────────────────────────────────────
const activeTab = ref('rules')
const loading = ref(true)

const rewards = ref<GameReward[]>([])
const rules = ref<GameRule[]>([])
const milestones = ref<GameMilestone[]>([])
const milestoneGroupFilter = ref('')

// Dialog state
const showRewardDialog = ref(false)
const showRuleDialog = ref(false)
const showMilestoneDialog = ref(false)

// Reward form
const rewardForm = ref({
  name: '',
  icon: '',
  pointvalue: 0,
  coupon: 0,
  asset: '',
})

// Rule form
const ruleForm = ref({
  label: '',
  action: 'media-upload-video',
  rewardid: 0,
  maxuses: 0,
  type: 0,
  destchannel: 0,
  destgroup: 0,
  destcollection: 0,
  parentid: 0,
  notificationid: 0,
  ruleOrder: 0,
  logtext: '',
})

// Milestone form
const milestoneForm = ref({
  label: '',
  actionid: 0,
  threshold: 1,
  rewardid: 0,
  grouping: '',
  notificationid: 0,
})
const newGrouping = ref('')
const useNewGrouping = ref(false)

// ─── Action categories for the rule builder ─────────────────
const actionCategories = [
  {
    label: 'Media Uploads',
    icon: 'upload',
    actions: [
      { value: 'media-upload-video', label: 'Video Upload' },
      { value: 'media-upload-image', label: 'Image/Photo Upload' },
      { value: 'media-upload-audio', label: 'Audio Upload' },
      { value: 'media-upload-text', label: 'Text Upload' },
    ],
  },
  {
    label: 'Media Interactions',
    icon: 'interaction',
    actions: [
      { value: 'media-view', label: 'Media Viewed' },
      { value: 'media-rated', label: 'Media Rated' },
      { value: 'media-voted-up', label: 'Media Voted Up' },
      { value: 'media-voted-down', label: 'Media Voted Down' },
      { value: 'media-comment', label: 'Commented on Media' },
      { value: 'media-commented', label: 'Media Commented On' },
      { value: 'watched-video', label: 'Watched Video' },
    ],
  },
  {
    label: 'Media Sharing',
    icon: 'share',
    actions: [
      { value: 'media-shared', label: 'Media Shared' },
      { value: 'media-shared-facebook', label: 'Shared on Facebook' },
      { value: 'media-shared-twitter', label: 'Shared on Twitter' },
      { value: 'media-shared-linkedin', label: 'Shared on LinkedIn' },
      { value: 'media-shared-whatsapp', label: 'Shared on WhatsApp' },
      { value: 'media-shared-reddit', label: 'Shared on Reddit' },
      { value: 'media-shared-pinterest', label: 'Shared on Pinterest' },
    ],
  },
  {
    label: 'User Actions',
    icon: 'user',
    actions: [
      { value: 'user-register', label: 'User Registered' },
      { value: 'user-updated-profile', label: 'Updated Profile' },
      { value: 'user-follow', label: 'Followed User' },
      { value: 'user-invited', label: 'Invited User To Join' },
      { value: 'user-invited-user-joined', label: 'Invited User Joined' },
      { value: 'user-sentconnections', label: 'Uploaded Contacts' },
    ],
  },
  {
    label: 'Social Links',
    icon: 'social',
    actions: [
      { value: 'user-social-account-link', label: 'Linked Social Account' },
      { value: 'user-social-account-link-facebook', label: 'Linked Facebook' },
      { value: 'user-social-account-link-twitter', label: 'Linked Twitter' },
      { value: 'user-social-account-link-instagram', label: 'Linked Instagram' },
      { value: 'user-social-account-link-youtube', label: 'Linked YouTube' },
      { value: 'user-social-account-link-apple', label: 'Linked Apple' },
    ],
  },
  {
    label: 'Moderation',
    icon: 'moderation',
    actions: [
      { value: 'media-moderation-1', label: 'Media Approved' },
      { value: 'media-moderation-2', label: 'Media Denied' },
      { value: 'media-converted-successfully', label: 'Successful Conversion' },
      { value: 'media-converted-failure', label: 'Failed Conversion' },
    ],
  },
  {
    label: 'Groups & Channels',
    icon: 'groups',
    actions: [
      { value: 'group-joined-member', label: 'Joined a Group' },
      { value: 'group-added-member', label: 'Added to Group' },
      { value: 'group-removed-member', label: 'Removed From Group' },
      { value: 'group-added-media', label: 'Media Added to Group' },
      { value: 'channel-added-media', label: 'Media Added to Channel' },
      { value: 'collection-added-media', label: 'Media Added to Collection' },
    ],
  },
  {
    label: 'Child Media',
    icon: 'child',
    actions: [
      { value: 'media-child-video', label: 'Submitted Child Video' },
      { value: 'media-child-photo', label: 'Submitted Child Photo' },
      { value: 'media-child-text', label: 'Submitted Child Text' },
      { value: 'media-child-audio', label: 'Submitted Child Audio' },
    ],
  },
  {
    label: 'Received Actions',
    icon: 'received',
    actions: [
      { value: 'media-vote-up-received', label: "User's Media Received Up Vote" },
      { value: 'media-vote-down-received', label: "User's Media Received Down Vote" },
      { value: 'media-rating-received', label: "User's Media Received Rating" },
      { value: 'media-message-mention', label: 'Username Mentioned in Media' },
    ],
  },
  {
    label: 'Other',
    icon: 'other',
    actions: [
      { value: 'media-field-changed', label: 'Media Field Changed' },
      { value: 'custom', label: 'Custom Action (API)' },
    ],
  },
]

const allActions = computed(() =>
  actionCategories.flatMap(c => c.actions)
)

const getActionLabel = (actionValue: string) =>
  allActions.value.find(a => a.value === actionValue)?.label ?? actionValue

const maxUsesOptions = [
  { value: '0', label: 'Unlimited' },
  { value: '-1', label: 'Once Per Subject' },
  ...Array.from({ length: 10 }, (_, i) => ({
    value: String(i + 1),
    label: String(i + 1),
  })),
]

// ─── Computed ─────────────────────────────────────────────────
const milestoneGroups = computed(() => {
  const groups = new Set(milestones.value.map(m => m.grouping).filter(Boolean))
  return Array.from(groups)
})

const filteredMilestones = computed(() => {
  if (!milestoneGroupFilter.value) return milestones.value
  return milestones.value.filter(m => m.grouping === milestoneGroupFilter.value)
})

const getRewardName = (rewardId: number) =>
  rewards.value.find(r => r.id === rewardId)?.name ?? `Reward #${rewardId}`

const getRewardIcon = (rewardId: number) =>
  rewards.value.find(r => r.id === rewardId)?.icon ?? ''

const getRuleLabel = (ruleId: number) =>
  rules.value.find(r => r.id === ruleId)?.label ?? `Rule #${ruleId}`

// ─── Stats ────────────────────────────────────────────────────
const totalPointsAvailable = computed(() =>
  rewards.value.reduce((sum, r) => sum + (r.pointvalue || 0), 0)
)

// ─── Data Loading ─────────────────────────────────────────────
const loadAll = async () => {
  if (!vhostId?.value) return
  loading.value = true
  try {
    const vid = Number(vhostId.value)
    const [r, ru, m] = await Promise.all([
      fetchRewards(vid),
      fetchRules(vid),
      fetchMilestones(vid),
    ])
    rewards.value = r
    rules.value = ru
    milestones.value = m
  } catch (e: any) {
    toast.error('Failed to load gamification data: ' + e.message)
  } finally {
    loading.value = false
  }
}

onMounted(loadAll)
watch(() => vhostId?.value, loadAll)

// ─── Reward Actions ───────────────────────────────────────────
const openRewardDialog = () => {
  rewardForm.value = { name: '', icon: '', pointvalue: 0, coupon: 0, asset: '' }
  showRewardDialog.value = true
}

const saveReward = async () => {
  if (!vhostId?.value || !rewardForm.value.name) return
  try {
    await createReward(Number(vhostId.value), { ...rewardForm.value })
    toast.success('Reward created')
    showRewardDialog.value = false
    await loadAll()
  } catch (e: any) {
    toast.error('Failed to create reward: ' + e.message)
  }
}

const removeReward = async (id: number) => {
  if (!vhostId?.value) return
  try {
    await deleteReward(Number(vhostId.value), id)
    toast.success('Reward deleted')
    await loadAll()
  } catch (e: any) {
    toast.error('Failed to delete reward: ' + e.message)
  }
}

// ─── Rule Actions ─────────────────────────────────────────────
const openRuleDialog = () => {
  ruleForm.value = {
    label: '', action: 'media-upload-video', rewardid: rewards.value[0]?.id ?? 0,
    maxuses: 0, type: 0, destchannel: 0, destgroup: 0, destcollection: 0,
    parentid: 0, notificationid: 0, ruleOrder: 0, logtext: '',
  }
  showRuleDialog.value = true
}

const saveRule = async () => {
  if (!vhostId?.value || !ruleForm.value.label) return
  try {
    await createRule(Number(vhostId.value), { ...ruleForm.value })
    toast.success('Scoring rule created')
    showRuleDialog.value = false
    await loadAll()
  } catch (e: any) {
    toast.error('Failed to create rule: ' + e.message)
  }
}

const removeRule = async (id: number) => {
  if (!vhostId?.value) return
  try {
    await deleteRule(Number(vhostId.value), id)
    toast.success('Rule deleted')
    await loadAll()
  } catch (e: any) {
    toast.error('Failed to delete rule: ' + e.message)
  }
}

// ─── Milestone Actions ────────────────────────────────────────
const openMilestoneDialog = () => {
  milestoneForm.value = {
    label: '', actionid: rules.value[0]?.id ?? 0,
    threshold: 1, rewardid: rewards.value[0]?.id ?? 0,
    grouping: milestoneGroups.value[0] ?? '', notificationid: 0,
  }
  useNewGrouping.value = false
  newGrouping.value = ''
  showMilestoneDialog.value = true
}

const saveMilestone = async () => {
  if (!vhostId?.value || !milestoneForm.value.label) return
  try {
    const data = {
      ...milestoneForm.value,
      grouping: useNewGrouping.value ? newGrouping.value : milestoneForm.value.grouping,
    }
    await createMilestone(Number(vhostId.value), data)
    toast.success('Milestone created')
    showMilestoneDialog.value = false
    await loadAll()
  } catch (e: any) {
    toast.error('Failed to create milestone: ' + e.message)
  }
}

const removeMilestone = async (id: number) => {
  if (!vhostId?.value) return
  try {
    await deleteMilestone(Number(vhostId.value), id)
    toast.success('Milestone deleted')
    await loadAll()
  } catch (e: any) {
    toast.error('Failed to delete milestone: ' + e.message)
  }
}
</script>

<template>
  <div class="space-y-5 px-4 pb-4">
    <!-- Stats strip -->
    <div class="grid grid-cols-4 gap-px bg-border rounded-md overflow-hidden">
      <div class="bg-background px-4 py-3 flex items-center justify-between">
        <span class="text-xs font-medium uppercase tracking-wider text-muted-foreground">Rules</span>
        <span class="text-xl font-semibold tabular-nums">{{ rules.length }}</span>
      </div>
      <div class="bg-background px-4 py-3 flex items-center justify-between">
        <span class="text-xs font-medium uppercase tracking-wider text-muted-foreground">Rewards</span>
        <span class="text-xl font-semibold tabular-nums">{{ rewards.length }}</span>
      </div>
      <div class="bg-background px-4 py-3 flex items-center justify-between">
        <span class="text-xs font-medium uppercase tracking-wider text-muted-foreground">Milestones</span>
        <span class="text-xl font-semibold tabular-nums">{{ milestones.length }}</span>
      </div>
      <div class="bg-background px-4 py-3 flex items-center justify-between">
        <span class="text-xs font-medium uppercase tracking-wider text-muted-foreground">Total Pts</span>
        <span class="text-xl font-semibold tabular-nums">{{ totalPointsAvailable }}</span>
      </div>
    </div>

    <!-- Main tabs -->
    <Tabs v-model="activeTab" class="w-full">
      <div class="flex items-center justify-between border-b pb-px">
        <TabsList class="bg-transparent p-0 h-auto gap-4">
          <TabsTrigger value="rules" class="bg-transparent rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-1 pb-2.5 pt-1 text-sm font-medium">
            Scoring Rules
          </TabsTrigger>
          <TabsTrigger value="rewards" class="bg-transparent rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-1 pb-2.5 pt-1 text-sm font-medium">
            Rewards
          </TabsTrigger>
          <TabsTrigger value="milestones" class="bg-transparent rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-1 pb-2.5 pt-1 text-sm font-medium">
            Milestones
          </TabsTrigger>
        </TabsList>
        <Button v-if="activeTab === 'rules'" @click="openRuleDialog" size="sm" class="gap-1.5 h-8">
          <Plus class="h-3.5 w-3.5" /> Add Rule
        </Button>
        <Button v-if="activeTab === 'rewards'" @click="openRewardDialog" size="sm" class="gap-1.5 h-8">
          <Plus class="h-3.5 w-3.5" /> Add Reward
        </Button>
        <Button v-if="activeTab === 'milestones'" @click="openMilestoneDialog" size="sm" class="gap-1.5 h-8">
          <Plus class="h-3.5 w-3.5" /> Add Milestone
        </Button>
      </div>

      <!-- ═══ SCORING RULES TAB ═══ -->
      <TabsContent value="rules" class="mt-0 pt-4">
        <div v-if="loading" class="flex items-center justify-center py-16">
          <div class="animate-spin rounded-full h-6 w-6 border-2 border-muted-foreground border-t-transparent" />
        </div>
        <Table v-else-if="rules.length > 0">
          <TableHeader>
            <TableRow class="hover:bg-transparent">
              <TableHead class="w-10 text-xs">#</TableHead>
              <TableHead class="text-xs">Label</TableHead>
              <TableHead class="text-xs">Trigger</TableHead>
              <TableHead class="text-xs">Reward</TableHead>
              <TableHead class="text-xs text-center">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger class="flex items-center gap-1 mx-auto">
                      Limit <HelpCircle class="h-3 w-3 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p class="max-w-xs text-xs">0 = unlimited, -1 = once per subject</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </TableHead>
              <TableHead class="text-xs text-center">Scope</TableHead>
              <TableHead class="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow v-for="rule in rules" :key="rule.id" class="group">
              <TableCell class="font-mono text-xs text-muted-foreground">{{ rule.id }}</TableCell>
              <TableCell class="font-medium text-sm">{{ rule.label }}</TableCell>
              <TableCell>
                <code class="text-xs bg-muted px-1.5 py-0.5 rounded-sm font-mono">{{ getActionLabel(rule.action) }}</code>
              </TableCell>
              <TableCell>
                <div class="flex items-center gap-1.5">
                  <img v-if="getRewardIcon(rule.rewardid)" :src="getRewardIcon(rule.rewardid)" class="h-4 w-4 rounded-sm" />
                  <span class="text-sm">{{ getRewardName(rule.rewardid) }}</span>
                </div>
              </TableCell>
              <TableCell class="text-center">
                <span v-if="rule.maxuses === 0" class="text-xs text-muted-foreground">&infin;</span>
                <span v-else-if="rule.maxuses === -1" class="text-xs text-muted-foreground">1/subj</span>
                <span v-else class="text-xs font-mono">{{ rule.maxuses }}x</span>
              </TableCell>
              <TableCell class="text-center">
                <div class="flex items-center justify-center gap-1">
                  <span v-if="rule.destchannel" class="text-[10px] font-mono bg-muted px-1 py-px rounded-sm">ch:{{ rule.destchannel }}</span>
                  <span v-if="rule.destgroup" class="text-[10px] font-mono bg-muted px-1 py-px rounded-sm">grp:{{ rule.destgroup }}</span>
                  <span v-if="rule.destcollection" class="text-[10px] font-mono bg-muted px-1 py-px rounded-sm">col:{{ rule.destcollection }}</span>
                  <span v-if="!rule.destchannel && !rule.destgroup && !rule.destcollection" class="text-xs text-muted-foreground">—</span>
                </div>
              </TableCell>
              <TableCell class="text-right">
                <Button variant="ghost" size="icon" @click="removeRule(rule.id)"
                  class="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive">
                  <Trash2 class="h-3.5 w-3.5" />
                </Button>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <div v-else class="flex flex-col items-center justify-center py-16 text-center">
          <div class="h-10 w-10 rounded-md bg-muted flex items-center justify-center mb-3">
            <Zap class="h-5 w-5 text-muted-foreground" />
          </div>
          <p class="text-sm font-medium">No scoring rules</p>
          <p class="text-xs text-muted-foreground mt-1 max-w-sm">Create rules to reward user actions with points</p>
          <Button @click="openRuleDialog" variant="outline" size="sm" class="mt-4 gap-1.5 h-8">
            <Plus class="h-3.5 w-3.5" /> Create Rule
          </Button>
        </div>
      </TabsContent>

      <!-- ═══ REWARDS TAB ═══ -->
      <TabsContent value="rewards" class="mt-0 pt-4">
        <div v-if="loading" class="flex items-center justify-center py-16">
          <div class="animate-spin rounded-full h-6 w-6 border-2 border-muted-foreground border-t-transparent" />
        </div>
        <div v-else-if="rewards.length > 0" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div v-for="reward in rewards" :key="reward.id"
            class="group relative border rounded-md p-4 hover:bg-muted/50 transition-colors">
            <div class="flex items-start gap-3">
              <div class="h-10 w-10 rounded-md bg-muted flex items-center justify-center flex-shrink-0 overflow-hidden">
                <img v-if="reward.icon" :src="reward.icon" class="h-8 w-8 object-contain" />
                <Award v-else class="h-5 w-5 text-muted-foreground" />
              </div>
              <div class="min-w-0 flex-1">
                <p class="text-sm font-medium truncate">{{ reward.name }}</p>
                <div class="flex items-center gap-2 mt-1">
                  <span class="text-xs font-mono text-muted-foreground">{{ reward.pointvalue }} pts</span>
                  <span v-if="reward.coupon" class="text-[10px] uppercase tracking-wider text-muted-foreground bg-muted px-1 py-px rounded-sm">coupon</span>
                  <span v-if="reward.asset" class="text-[10px] uppercase tracking-wider text-muted-foreground bg-muted px-1 py-px rounded-sm">asset</span>
                </div>
              </div>
              <Button variant="ghost" size="icon" @click="removeReward(reward.id)"
                class="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive flex-shrink-0">
                <Trash2 class="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
        <div v-else class="flex flex-col items-center justify-center py-16 text-center">
          <div class="h-10 w-10 rounded-md bg-muted flex items-center justify-center mb-3">
            <Gift class="h-5 w-5 text-muted-foreground" />
          </div>
          <p class="text-sm font-medium">No rewards configured</p>
          <p class="text-xs text-muted-foreground mt-1 max-w-sm">Create rewards first, then reference them in scoring rules</p>
          <Button @click="openRewardDialog" variant="outline" size="sm" class="mt-4 gap-1.5 h-8">
            <Plus class="h-3.5 w-3.5" /> Create Reward
          </Button>
        </div>
      </TabsContent>

      <!-- ═══ MILESTONES TAB ═══ -->
      <TabsContent value="milestones" class="mt-0 pt-4">
        <div v-if="loading" class="flex items-center justify-center py-16">
          <div class="animate-spin rounded-full h-6 w-6 border-2 border-muted-foreground border-t-transparent" />
        </div>
        <template v-else-if="milestones.length > 0">
          <!-- Group filter -->
          <div v-if="milestoneGroups.length > 0" class="flex items-center gap-2 mb-4">
            <span class="text-xs text-muted-foreground">Group:</span>
            <div class="flex gap-1 flex-wrap">
              <button
                @click="milestoneGroupFilter = ''"
                :class="[
                  'px-2 py-0.5 text-xs rounded-sm border transition-colors',
                  milestoneGroupFilter === '' ? 'bg-primary text-primary-foreground border-primary' : 'bg-transparent border-border text-muted-foreground hover:text-foreground'
                ]"
              >All</button>
              <button
                v-for="group in milestoneGroups"
                :key="group"
                @click="milestoneGroupFilter = group"
                :class="[
                  'px-2 py-0.5 text-xs rounded-sm border transition-colors',
                  milestoneGroupFilter === group ? 'bg-primary text-primary-foreground border-primary' : 'bg-transparent border-border text-muted-foreground hover:text-foreground'
                ]"
              >{{ group }}</button>
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow class="hover:bg-transparent">
                <TableHead class="text-xs">Label</TableHead>
                <TableHead class="text-xs">Group</TableHead>
                <TableHead class="text-xs text-center">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger class="flex items-center gap-1 mx-auto">
                        Threshold <HelpCircle class="h-3 w-3 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p class="max-w-xs text-xs">Times the linked rule must trigger to unlock</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </TableHead>
                <TableHead class="text-xs">Reward</TableHead>
                <TableHead class="text-xs">Linked Rule</TableHead>
                <TableHead class="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow v-for="m in filteredMilestones" :key="m.id" class="group">
                <TableCell class="font-medium text-sm">{{ m.label }}</TableCell>
                <TableCell>
                  <code v-if="m.grouping" class="text-xs bg-muted px-1.5 py-0.5 rounded-sm font-mono">{{ m.grouping }}</code>
                  <span v-else class="text-xs text-muted-foreground">—</span>
                </TableCell>
                <TableCell class="text-center font-mono text-sm">{{ m.threshold }}</TableCell>
                <TableCell>
                  <div class="flex items-center gap-1.5">
                    <img v-if="getRewardIcon(m.rewardid)" :src="getRewardIcon(m.rewardid)" class="h-4 w-4 rounded-sm" />
                    <span class="text-sm">{{ getRewardName(m.rewardid) }}</span>
                  </div>
                </TableCell>
                <TableCell class="text-sm text-muted-foreground">{{ getRuleLabel(m.actionid) }}</TableCell>
                <TableCell class="text-right">
                  <Button variant="ghost" size="icon" @click="removeMilestone(m.id)"
                    class="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive">
                    <Trash2 class="h-3.5 w-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </template>
        <div v-else class="flex flex-col items-center justify-center py-16 text-center">
          <div class="h-10 w-10 rounded-md bg-muted flex items-center justify-center mb-3">
            <Target class="h-5 w-5 text-muted-foreground" />
          </div>
          <p class="text-sm font-medium">No milestones set up</p>
          <p class="text-xs text-muted-foreground mt-1 max-w-sm">Milestones reward cumulative achievements like "Upload 50 videos"</p>
          <Button @click="openMilestoneDialog" variant="outline" size="sm" class="mt-4 gap-1.5 h-8">
            <Plus class="h-3.5 w-3.5" /> Create Milestone
          </Button>
        </div>
      </TabsContent>
    </Tabs>

    <!-- ═══ CREATE REWARD DIALOG ═══ -->
    <Dialog v-model:open="showRewardDialog">
      <DialogContent class="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New Reward</DialogTitle>
          <DialogDescription>Define what users earn — points, badges, or coupons.</DialogDescription>
        </DialogHeader>
        <div class="space-y-4 py-2">
          <div class="space-y-1.5">
            <Label class="text-xs">Reward Name</Label>
            <Input v-model="rewardForm.name" placeholder="e.g. Gold Star, Super Uploader" />
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div class="space-y-1.5">
              <Label class="text-xs">Point Value</Label>
              <Input v-model.number="rewardForm.pointvalue" type="number" min="0" />
            </div>
            <div class="space-y-1.5">
              <Label class="text-xs">Attach Coupon</Label>
              <Select :model-value="String(rewardForm.coupon)" @update:model-value="(v: string) => rewardForm.coupon = Number(v)">
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">No</SelectItem>
                  <SelectItem value="1">Yes</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div class="space-y-1.5">
            <Label class="text-xs">Badge Icon URL</Label>
            <Input v-model="rewardForm.icon" placeholder="https://..." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" @click="showRewardDialog = false">Cancel</Button>
          <Button size="sm" @click="saveReward" :disabled="!rewardForm.name">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <!-- ═══ CREATE RULE DIALOG ═══ -->
    <Dialog v-model:open="showRuleDialog">
      <DialogContent class="sm:max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New Scoring Rule</DialogTitle>
          <DialogDescription>Link a user action to a reward. When the action occurs, the user earns points.</DialogDescription>
        </DialogHeader>
        <div class="space-y-4 py-2">
          <div class="space-y-1.5">
            <Label class="text-xs">Rule Label</Label>
            <Input v-model="ruleForm.label" placeholder="e.g. Upload Video Reward, Comment Bonus" />
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div class="space-y-1.5">
              <Label class="text-xs">Trigger Action</Label>
              <Select v-model="ruleForm.action">
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <template v-for="category in actionCategories" :key="category.label">
                    <div class="px-2 py-1 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{{ category.label }}</div>
                    <SelectItem v-for="action in category.actions" :key="action.value" :value="action.value">
                      {{ action.label }}
                    </SelectItem>
                  </template>
                </SelectContent>
              </Select>
            </div>
            <div class="space-y-1.5">
              <Label class="text-xs">Reward</Label>
              <Select :model-value="String(ruleForm.rewardid)" @update:model-value="(v: string) => ruleForm.rewardid = Number(v)">
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="reward in rewards" :key="reward.id" :value="String(reward.id)">
                    {{ reward.name }} ({{ reward.pointvalue }} pts)
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div class="space-y-1.5">
              <Label class="text-xs">Max Uses</Label>
              <Select :model-value="String(ruleForm.maxuses)" @update:model-value="(v: string) => ruleForm.maxuses = Number(v)">
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="opt in maxUsesOptions" :key="opt.value" :value="opt.value">
                    {{ opt.label }}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div class="space-y-1.5">
              <Label class="text-xs">Processing Order</Label>
              <Input v-model.number="ruleForm.ruleOrder" type="number" min="0" />
            </div>
          </div>

          <Separator />

          <div class="space-y-2">
            <Label class="text-xs uppercase tracking-wider text-muted-foreground">Scope (optional)</Label>
            <div class="grid grid-cols-3 gap-3">
              <div class="space-y-1">
                <Label class="text-[10px] text-muted-foreground">Channel ID</Label>
                <Input v-model.number="ruleForm.destchannel" type="number" min="0" placeholder="0" />
              </div>
              <div class="space-y-1">
                <Label class="text-[10px] text-muted-foreground">Group ID</Label>
                <Input v-model.number="ruleForm.destgroup" type="number" min="0" placeholder="0" />
              </div>
              <div class="space-y-1">
                <Label class="text-[10px] text-muted-foreground">Collection ID</Label>
                <Input v-model.number="ruleForm.destcollection" type="number" min="0" placeholder="0" />
              </div>
            </div>
          </div>

          <div class="space-y-1.5">
            <Label class="text-xs">Log Text</Label>
            <Input v-model="ruleForm.logtext" placeholder="e.g. {user.firstname} uploaded a video" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" @click="showRuleDialog = false">Cancel</Button>
          <Button size="sm" @click="saveRule" :disabled="!ruleForm.label || !ruleForm.rewardid">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <!-- ═══ CREATE MILESTONE DIALOG ═══ -->
    <Dialog v-model:open="showMilestoneDialog">
      <DialogContent class="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New Milestone</DialogTitle>
          <DialogDescription>Grant a bonus reward when a user hits a cumulative threshold.</DialogDescription>
        </DialogHeader>
        <div class="space-y-4 py-2">
          <div class="space-y-1.5">
            <Label class="text-xs">Milestone Label</Label>
            <Input v-model="milestoneForm.label" placeholder="e.g. Prolific Uploader, 100 Views Club" />
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div class="space-y-1.5">
              <Label class="text-xs">Linked Rule</Label>
              <Select :model-value="String(milestoneForm.actionid)" @update:model-value="(v: string) => milestoneForm.actionid = Number(v)">
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="rule in rules" :key="rule.id" :value="String(rule.id)">
                    {{ rule.label }}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div class="space-y-1.5">
              <Label class="text-xs">Threshold</Label>
              <Input v-model.number="milestoneForm.threshold" type="number" min="1" />
            </div>
          </div>
          <div class="space-y-1.5">
            <Label class="text-xs">Completion Reward</Label>
            <Select :model-value="String(milestoneForm.rewardid)" @update:model-value="(v: string) => milestoneForm.rewardid = Number(v)">
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem v-for="reward in rewards" :key="reward.id" :value="String(reward.id)">
                  {{ reward.name }} ({{ reward.pointvalue }} pts)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div class="space-y-1.5">
            <Label class="text-xs">Progression Group</Label>
            <div v-if="!useNewGrouping" class="flex gap-2">
              <Select v-model="milestoneForm.grouping" class="flex-1">
                <SelectTrigger><SelectValue placeholder="Select group..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="group in milestoneGroups" :key="group" :value="group">
                    {{ group }}
                  </SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" @click="useNewGrouping = true" class="h-9 text-xs">+ New</Button>
            </div>
            <div v-else class="flex gap-2">
              <Input v-model="newGrouping" placeholder="New group name" class="flex-1" />
              <Button variant="outline" size="sm" @click="useNewGrouping = false" class="h-9 text-xs">Existing</Button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" @click="showMilestoneDialog = false">Cancel</Button>
          <Button size="sm" @click="saveMilestone" :disabled="!milestoneForm.label">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>
